import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { FaCrown, FaBox, FaRecycle, FaMapMarkerAlt, FaTag, FaHeart, FaEye } from 'react-icons/fa';
import { getMediaUrl } from '../utils/urlHelpers';
import { 
  isProductPromoted, 
  getPromotionTypeLabel, 
  getPromotionBadgeClass,
  hasActivePromotion
} from '../utils/promotionUtils';
import '../styles/product-cards-v2.css';
import error from '../assets/Tr3.jpg';
import { InfinityLoadingSkeleton } from './Skeletons';

const CustomCarouselV2 = ({ products, onProductClick, handleLikeClick, loggedInUser, loadingMore }) => {
    const [likedProducts, setLikedProducts] = useState([]);

    useEffect(() => {
        if (loggedInUser && products) {
            const liked = products
                .filter((product) => product.liked_by?.includes(loggedInUser.id))
                .map((product) => product.id);
            setLikedProducts(liked);
        }
    }, [products, loggedInUser]);

    const handleLocalLikeClick = async (e, productId) => {
        e.preventDefault();
        e.stopPropagation();

        const isLikedByUser = likedProducts.includes(productId);

        if (typeof handleLikeClick === 'function') {
            await handleLikeClick(productId, isLikedByUser);
        }
    };

    const handleImageError = (e) => {
        console.error('Image failed to load:', e.target.src);
        e.target.src = error;
    };

    const shouldShowNavigation = (slidesPerView) => {
        return products.length > slidesPerView;
    };

    const formatPrice = (price) => {
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        
        if (typeof numPrice !== 'number' || isNaN(numPrice) || numPrice < 0) {
            return '0';
        }
        
        if (numPrice >= 1000) {
            return `${(numPrice / 1000).toLocaleString('fr-FR', {
                maximumFractionDigits: 1,
                minimumFractionDigits: 0
            })}k`;
        }
        return numPrice.toLocaleString('fr-FR', {
            maximumFractionDigits: 0
        });
    };

    if (!products || products.length === 0) {
        return <InfinityLoadingSkeleton />;
    }

    return (
        <>
            <Swiper
                modules={[Autoplay, Pagination, Navigation]}
                spaceBetween={20}
                slidesPerView={4}
                pagination={{
                    clickable: true,
                    el: '.swiper-pagination',
                }}
                navigation={{
                    nextEl: shouldShowNavigation(4) ? '.swiper-button-next' : null,
                    prevEl: shouldShowNavigation(4) ? '.swiper-button-prev' : null,
                }}
                breakpoints={{
                    // Mobile phones
                    320: {
                        slidesPerView: 2.2,
                        spaceBetween: 12,
                        navigation: {
                            nextEl: shouldShowNavigation(1) ? '.swiper-button-next' : null,
                            prevEl: shouldShowNavigation(1) ? '.swiper-button-prev' : null,
                        },
                    },
                    // Large phones
                    480: {
                        slidesPerView: 2.5,
                        spaceBetween: 14,
                        navigation: {
                            nextEl: shouldShowNavigation(2) ? '.swiper-button-next' : null,
                            prevEl: shouldShowNavigation(2) ? '.swiper-button-prev' : null,
                        },
                    },
                    // Tablets
                    768: {
                        slidesPerView: 3,
                        spaceBetween: 16,
                        navigation: {
                            nextEl: shouldShowNavigation(3) ? '.swiper-button-next' : null,
                            prevEl: shouldShowNavigation(3) ? '.swiper-button-prev' : null,
                        },
                    },
                    // Small laptops
                    1024: {
                        slidesPerView: 4,
                        spaceBetween: 18,
                        navigation: {
                            nextEl: shouldShowNavigation(4) ? '.swiper-button-next' : null,
                            prevEl: shouldShowNavigation(4) ? '.swiper-button-prev' : null,
                        },
                    },
                    // Desktops
                    1280: {
                        slidesPerView: 5,
                        spaceBetween: 20,
                        navigation: {
                            nextEl: shouldShowNavigation(5) ? '.swiper-button-next' : null,
                            prevEl: shouldShowNavigation(5) ? '.swiper-button-prev' : null,
                        },
                    },
                    // Large screens
                    1536: {
                        slidesPerView: 6,
                        spaceBetween: 22,
                        navigation: {
                            nextEl: shouldShowNavigation(6) ? '.swiper-button-next' : null,
                            prevEl: shouldShowNavigation(6) ? '.swiper-button-prev' : null,
                        },
                    }
                }}
            >
                {products.map((product) => {
                    const isLikedByUser = likedProducts.includes(product.id);
                    const isOwner = loggedInUser && product.owner === loggedInUser.id;
                    // More robust promotion check - use both isProductPromoted and hasActivePromotion
                    const isPromoted = isProductPromoted(product) && hasActivePromotion(product);
                    const promotionLabel = getPromotionTypeLabel(product);
                    const formattedPrice = formatPrice(product.price);
                    const priceDisplay = product.negotiable
                        ? `À partir de ${product.currency} ${formattedPrice}`
                        : `${product.currency} ${formattedPrice}`;

                    return (
                        <SwiperSlide key={product.id}>
                            <div 
                                className="product-card-v2"
                                onClick={() => onProductClick(product.uuid)}  // Use product.uuid
                            >
                                {/* Image Section */}
                                <div className="product-image-section-v2">
                                    <img
                                        src={
                                            product.first_image
                                                ? getMediaUrl(product.first_image)
                                                : product.images && product.images.length > 0
                                                    ? getMediaUrl(product.images[0].url || product.images[0].image)
                                                    : product.media && product.media.length > 0
                                                        ? getMediaUrl(product.media[0].url || product.media[0].image)
                                                        : error
                                        }
                                        alt={product.name}
                                        className="product-image-v2"
                                        loading="lazy"
                                        onError={handleImageError}
                                    />
                                    
                                    {/* Price Overlay */}
                                    <div className="product-price-overlay-v2">
                                        {priceDisplay}
                                    </div>

                                    {/* Condition Badge */}
                                    <div className={`product-condition-badge-v2 ${product.condition === 'Usagé' ? 'used' : 'new'}`}>
                                        {product.condition === 'Usagé' ? (
                                            <FaRecycle />
                                        ) : (
                                            <FaBox />
                                        )}
                                    </div>

                                    {/* Promoted Badge */}
                                    {isPromoted && (
                                        <div className="product-promoted-badge-v2">
                                            <FaCrown />
                                            <span>{promotionLabel}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Content Section */}
                                <div className="product-content-v2">
                                    {/* Product Name */}
                                    <h3 className="product-name-v2">
                                        {product.name}
                                    </h3>

                                    {/* Product Description */}
                                    <div 
                                        className="product-description-v2"
                                        dangerouslySetInnerHTML={{ __html: product.description }}
                                    />

                                    {/* Meta Section */}
                                    <div className="product-meta-v2">
                                        {/* Location and Category */}
                                        <div className="product-location-category-v2">
                                            <div className="product-location-v2">
                                                <FaMapMarkerAlt className="icon" />
                                                <span>{product.city_name}</span>
                                            </div>
                                            <div className="product-category-v2">
                                                <FaTag className="icon" />
                                                <span>{product.category_name}</span>
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="product-stats-v2">
                                            <div
                                                className={`product-likes-v2 ${isLikedByUser ? 'liked' : ''}`}
                                                onClick={(e) => handleLocalLikeClick(e, product.uuid || product.id)}
                                            >
                                                <FaHeart className="icon" />
                                                {product.likes > 0 ? <span>{product.likes}</span> : null}
                                            </div>
                                            <div className="product-views-v2">
                                                <FaEye className="icon" />
                                                {product.views > 0 ? <span>{product.views}</span> : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
                <div className="swiper-pagination"></div>
                <div className="swiper-button-prev"></div>
                <div className="swiper-button-next"></div>
            </Swiper>
            {/* Show skeleton when loading more carousel items */}
            {loadingMore && <InfinityLoadingSkeleton />}
        </>
    );
};

export default CustomCarouselV2;